import axios, { AxiosError } from "axios";
import type { AxiosResponse, InternalAxiosRequestConfig } from "axios";
import { LOGIN_EXPIRED_STORAGE_KEY } from '@/lib/loginExpiredStorage';
import type { WrapResponse } from '@/types';

/** 业务码：登录已过期，需重新登录 */
const LOGIN_EXPIRED_CODE = 4001;
const DEFAULT_REQUEST_ERROR_MESSAGE = '请求失败，请稍后重试';

let loginExpiredRedirecting = false;

export function normalizeDisplayMessage(value: unknown, fallback = DEFAULT_REQUEST_ERROR_MESSAGE): string {
  if (typeof value === 'string') {
    return value.trim() || fallback;
  }
  if (value instanceof Error) {
    return value.message || fallback;
  }
  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }
  if (value && typeof value === 'object' && 'message' in value) {
    const message = (value as { message?: unknown }).message;
    if (typeof message === 'string' && message.trim()) {
      return message;
    }
  }
  return fallback;
}

function isLoginExpiredBody(data: unknown): data is WrapResponse {
  return (
    typeof data === 'object' &&
    data !== null &&
    'code' in data &&
    (data as WrapResponse).code === LOGIN_EXPIRED_CODE
  );
}

function normalizeResponseMessage(data: unknown) {
  if (!data || typeof data !== 'object' || !('message' in data)) {
    return;
  }
  (data as { message: string }).message = normalizeDisplayMessage(
    (data as { message?: unknown }).message,
  );
}

function handleLoginExpired(message: string) {
  if (typeof window === 'undefined' || loginExpiredRedirecting) return;
  loginExpiredRedirecting = true;
  try {
    const payload = {
      source: 'session_expired' as const,
      message: message || '登录已过期，请重新登录',
    };
    sessionStorage.setItem(LOGIN_EXPIRED_STORAGE_KEY, JSON.stringify(payload));
  } catch {
    /* 无痕模式等 */
  }
  window.location.assign('/login');
}

function rejectSessionExpired(
  config: InternalAxiosRequestConfig,
  response: AxiosResponse,
  message: string
) {
  const msg = message || '登录已过期，请重新登录';
  return Promise.reject(
    new AxiosError(
      msg,
      'ERR_SESSION_EXPIRED',
      config,
      undefined,
      { ...response, data: { ...response.data, code: LOGIN_EXPIRED_CODE, message: msg } as WrapResponse }
    )
  );
}

export const fun = axios.create({
  baseURL: typeof window !== 'undefined' ? `https://${process.env.NEXT_PUBLIC_API_HOST}` : '',
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

// 响应拦截器：统一处理服务端异常和网络错误
fun.interceptors.response.use(
  (response: AxiosResponse) => {
    const contentType = response.headers['content-type'] || '';
    const responseData = response.data;
    const responseType = response.config?.responseType;

    // 如果是 blob 响应（用于文件下载），直接返回，不进行 JSON 处理
    if (responseType === 'blob' || responseData instanceof Blob) {
      return response;
    }

    // 检查响应是否为 JSON 格式
    if (!contentType.includes('application/json')) {
      const errorResponse: WrapResponse<{}> = {
        code: response.status || 500,
        message: '系统繁忙，请稍后再试。',
        data: {},
      };

      response.data = errorResponse;
      return response;
    }

    // 如果响应是 JSON，检查是否为字符串（需要解析）
    if (typeof responseData === 'string') {
      try {
        response.data = JSON.parse(responseData);
      } catch (e) {
        const errorResponse: WrapResponse<{}> = {
          code: 500,
          message: '系统繁忙，请稍后再试。',
          data: {},
        };
        response.data = errorResponse;
        return response;
      }
    }

    if (!(response.data instanceof Blob)) {
      normalizeResponseMessage(response.data);
    }

    if (!(response.data instanceof Blob) && isLoginExpiredBody(response.data)) {
      const body = response.data as WrapResponse;
      handleLoginExpired(body.message);
      return rejectSessionExpired(response.config, response, body.message);
    }

    return response;
  },
  (error: AxiosError) => {
    // 处理网络错误或其他 axios 错误
    if (error.response) {
      const contentType = error.response.headers['content-type'] || '';
      const responseData = error.response.data;
      const responseType = error.config?.responseType;

      // 如果是 blob 响应，直接返回错误，不进行 JSON 处理
      if (responseType === 'blob' || responseData instanceof Blob) {
        return Promise.reject(error);
      }

      if (!contentType.includes('application/json')) {
        const errorResponse: WrapResponse<{}> = {
          code: error.response.status || 500,
          message: typeof responseData === 'string'
            ? '系统繁忙，请稍后再试。'
            : '系统繁忙，请稍后再试。',
          data: {},
        };
        error.response.data = errorResponse;
      } else if (typeof responseData === 'string') {
        try {
          error.response.data = JSON.parse(responseData);
        } catch (e) {
          error.response.data = {
            code: error.response.status || 500,
            message: '系统繁忙，请稍后再试。',
            data: {},
          };
        }
      }
      normalizeResponseMessage(error.response.data);
    } else if (error.request) {
      // 请求已发出但没有收到响应（网络错误）
      const errorResponse: WrapResponse<{}> = {
        code: 0,
        message: '网络请求失败，请检查网络连接',
        data: {},
      };
      error.response = {
        data: errorResponse,
        status: 0,
        statusText: 'Network Error',
        headers: {},
        config: error.config!,
      } as AxiosResponse;
    } else {
      // 请求配置错误
      const errorResponse: WrapResponse<{}> = {
        code: 0,
        message: error.message || '请求配置错误',
        data: {},
      };
      error.response = {
        data: errorResponse,
        status: 0,
        statusText: 'Request Error',
        headers: {},
        config: error.config!,
      } as AxiosResponse;
    }

    const res = error.response;
    if (res && isLoginExpiredBody(res.data)) {
      const body = res.data as WrapResponse;
      handleLoginExpired(body.message);
      error.message = body.message;
    }

    return Promise.reject(error);
  }
);
